import { ChangeEvent, FC } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { ColorOptionLabel, MiDetailFields } from "@miplatform/components";
import { CONFIGURABLE_COLORS, INPUT_TYPES } from "@miplatform/constants";
import { Colors } from "@miplatform/types";
import { getContrastColor } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  TRAY_SORT_OPTIONS,
  TRAY_TYPE_OPTIONS,
} from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";
import { TrayPreviewHeader } from "./TrayPreviewHeader";

export const CalendarTrayTabView: FC<CalendarTrayTabViewProps> = ({
  form,
  extraTabProps: { isReadOnly, id } = {},
}: CalendarTrayTabViewProps) => {
  const { classes, theme } = useStyles();
  const { control, setValue, watch } = form;
  const trays = watch("trays");
  const index = trays.findIndex((tray) => tray.id === id) || 0;
  const selectedTray = trays[index];

  const trayFields = [
    {
      label: "Tray Name",
      name: `trays.${index}.name`,
      formControl: control,
      required: true,
      value: selectedTray?.name,
      disabled: isReadOnly || selectedTray?.isDefault,
      width: "20%",
    },
    {
      label: "Type",
      name: `trays.${index}.filter`,
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: TRAY_TYPE_OPTIONS,
      value: selectedTray?.filter,
      disabled: true,
      width: "20%",
    },
    {
      label: "Sort Order",
      name: `trays.${index}.sort`,
      formControl: control,
      value: selectedTray?.sort,
      fieldType: INPUT_TYPES.SELECT,
      options: TRAY_SORT_OPTIONS,
      required: true,
      disabled: isReadOnly,
      width: "20%",
    },
    {
      label: "Header Color",
      name: `trays.${index}.headerBackgroundColor`,
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      value: selectedTray?.headerBackgroundColor,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      disabled: isReadOnly,
      handleChange: ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setValue(`trays.${index}.headerBackgroundColor`, value, {
          shouldDirty: true,
        });
        setValue(`trays.${index}.headerColor`, getContrastColor(value), {
          shouldDirty: true,
        });
      },
      width: "20%",
    },
    {
      label: "Chip Color",
      name: `trays.${index}.countBackgroundColor`,
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      value: selectedTray?.countBackgroundColor,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      handleChange: ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setValue(`trays.${index}.countBackgroundColor`, value, {
          shouldDirty: true,
        });
        setValue(`trays.${index}.countColor`, getContrastColor(value), {
          shouldDirty: true,
        });
      },
      disabled: isReadOnly,
      width: "20%",
    },
  ];

  const additionalTrayFields = [
    {
      label: "Active",
      name: `trays.${index}.isActive`,
      formControl: control,
      required: true,
      value: selectedTray?.isActive,
      fieldType: INPUT_TYPES.SWITCH,
      handleChange: ({
        target: { checked },
      }: ChangeEvent<HTMLInputElement>) => {
        setValue(`trays.${index}.isActive`, checked, {
          shouldDirty: true,
        });
      },
      disabled: isReadOnly,
      width: 100,
      style: { marginTop: theme.spacing(0.5), minWidth: 120 },
    },
    {
      label: "Hide Tray When Empty",
      name: `trays.${index}.hideWhenEmpty`,
      formControl: control,
      required: true,
      value: selectedTray?.hideWhenEmpty,
      fieldType: INPUT_TYPES.CHECKBOX,
      handleChange: ({
        target: { checked },
      }: ChangeEvent<HTMLInputElement>) => {
        setValue(`trays.${index}.hideWhenEmpty`, checked, {
          shouldDirty: true,
        });
      },
      disabled: isReadOnly,
      width: 230,
    },
    {
      label: "Tray Can Be Collapsed",
      name: `trays.${index}.collapsible`,
      formControl: control,
      value: selectedTray?.collapsible,
      fieldType: INPUT_TYPES.CHECKBOX,
      required: true,
      disabled: isReadOnly,
      width: 220,
    },
  ];

  return (
    <>
      <ConfigAccordionHeader title={`${selectedTray?.name} Tray`}>
        <MiDetailFields detailFields={trayFields} />
        <Box className={classes.additionalFieldsContainer}>
          <Typography fontSize="inherit">Additional Configurations</Typography>
          <MiDetailFields detailFields={additionalTrayFields} />
        </Box>
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Tray Preview" color={Colors.Secondary}>
        <TrayPreviewHeader tray={selectedTray} />
      </ConfigAccordionHeader>
    </>
  );
};

interface CalendarTrayTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    id?: string;
  };
}

const useStyles = makeStyles()((theme) => ({
  additionalFieldsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: theme.fontSize.medium,
    paddingLeft: theme.spacing(2),
  },
}));
